import React, { useState, useEffect } from "react";
import { db } from "@lib/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import {
  LuCalendarDays,
  LuMapPin,
  LuClock,
  LuActivity,
  LuTrash2,
  LuPlus,
  LuSave,
  LuLoader,
  LuSettings,
  LuPencil,
  LuX,
  LuChevronDown,
  LuChevronUp
} from "react-icons/lu";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

export const EventManagement = () => {
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();
  const { getIdToken } = useAuth();
  const EVENT_ID = "ULTRA24";

  // Temporary state for new rundown items per category index
  const [newRundownInputs, setNewRundownInputs] = useState({});
  // State to track which rundown item is being edited { [catIndex]: rundownIndex }
  const [editingRundownIndices, setEditingRundownIndices] = useState({});
  // State to track expanded categories { [index]: boolean }
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isGeneralInfoExpanded, setIsGeneralInfoExpanded] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const docRef = doc(db, "semuaKegiatan", EVENT_ID);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setEventData(docSnap.data());
        } else {
          showNotification("error", "Event data not found");
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        showNotification("error", "Failed to load event data");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [showNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (index, field, value) => {
    const updatedCategories = [...eventData.categories];
    updatedCategories[index] = {
      ...updatedCategories[index],
      [field]: value,
    };
    setEventData((prev) => ({
      ...prev,
      categories: updatedCategories,
    }));
  };

  const handleToggleCategoryStatus = (index) => {
    const updatedCategories = [...eventData.categories];
    updatedCategories[index] = {
      ...updatedCategories[index],
      isRegistrationOpen: !updatedCategories[index].isRegistrationOpen,
    };
    setEventData((prev) => ({
      ...prev,
      categories: updatedCategories,
    }));
  };
  
  const toggleCategory = (index) => {
    setExpandedCategories(prev => ({
        ...prev,
        [index]: !prev[index]
    }));
  };

  // --- RUNDOWN LOGIC ---

  const handleRundownInputChange = (catIndex, field, value) => {
    setNewRundownInputs((prev) => ({
      ...prev,
      [catIndex]: {
        ...prev[catIndex],
        [field]: value,
      },
    }));
  };

  const handleAddRundown = (catIndex) => {
    const input = newRundownInputs[catIndex];
    if (!input || !input.activity || !input.time) {
      showNotification("error", "Activity and Time are required for rundown");
      return;
    }

    const updatedCategories = [...eventData.categories];
    const currentRundown = updatedCategories[catIndex].rundown || [];
    

    const newItem = {
        activity: input.activity,
        time: input.time,
        date: input.date || "", // Optional
        location: input.location || "" // Optional
    };

    const newRundownList = [...currentRundown, newItem];
    
    // Auto-sort items by Date then Time
    newRundownList.sort((a, b) => {
         const dateA = new Date(a.date).getTime();
         const dateB = new Date(b.date).getTime();
         if (dateA !== dateB) return dateA - dateB;
         return a.time.localeCompare(b.time);
    });

    updatedCategories[catIndex] = {
      ...updatedCategories[catIndex],
      rundown: newRundownList,
    };

    setEventData((prev) => ({
      ...prev,
      categories: updatedCategories,
    }));

    // Reset input for this category
    setNewRundownInputs((prev) => ({
      ...prev,
      [catIndex]: {
        date: input.date, // Keep date for convenience
        time: "",
        activity: "",
        location: "Race Venue"
      },
    }));
  };

  const handleDeleteRundown = (catIndex, rundownIndex) => {
    const updatedCategories = [...eventData.categories];
    const currentRundown = [...(updatedCategories[catIndex].rundown || [])];
    currentRundown.splice(rundownIndex, 1);
    
    updatedCategories[catIndex] = {
      ...updatedCategories[catIndex],
      rundown: currentRundown,
    };

    setEventData((prev) => ({
      ...prev,
      categories: updatedCategories,
    }));
    
    // If deleted item was being edited, cancel edit
    if (editingRundownIndices[catIndex] === rundownIndex) {
        handleCancelEdit(catIndex);
    }
  };

  const handleEditRundown = (catIndex, rundownIndex) => {
    setEditingRundownIndices((prev) => ({
        ...prev,
        [catIndex]: rundownIndex
    }));
    // Populate form with existing data
    const itemToEdit = eventData.categories[catIndex].rundown[rundownIndex];
    setNewRundownInputs((prev) => ({
        ...prev,
        [catIndex]: { ...itemToEdit }
    }));
  };

  const handleUpdateRundown = (catIndex) => {
    const rundownIndex = editingRundownIndices[catIndex];
    if (rundownIndex === undefined) return;

    const input = newRundownInputs[catIndex];
    if (!input?.date || !input?.time || !input?.activity) {
      showNotification("error", "Activity, Date, and Time are required");
      return;
    }

    const updatedCategories = [...eventData.categories];
    const currentRundown = [...(updatedCategories[catIndex].rundown || [])];
    
    currentRundown[rundownIndex] = {
        activity: input.activity,
        time: input.time,
        date: input.date || "",
        location: input.location || ""
    };

    // Auto-sort items by Date then Time
    currentRundown.sort((a, b) => {
         const dateA = new Date(a.date).getTime();
         const dateB = new Date(b.date).getTime();
         if (dateA !== dateB) return dateA - dateB;
         return a.time.localeCompare(b.time);
    });

    updatedCategories[catIndex] = {
      ...updatedCategories[catIndex],
      rundown: currentRundown,
    };

    setEventData((prev) => ({
      ...prev,
      categories: updatedCategories,
    }));

    handleCancelEdit(catIndex);
  };

  const handleCancelEdit = (catIndex) => {
    setNewRundownInputs((prev) => ({
        ...prev,
        [catIndex]: { activity: "", time: "", date: "", location: "" }
    }));
    setEditingRundownIndices((prev) => {
        const newState = { ...prev };
        delete newState[catIndex];
        return newState;
    });
  };

  // --- END RUNDOWN LOGIC ---

  const handleAddCategory = () => {
    setEventData((prev) => ({
      ...prev,
      categories: [
        ...prev.categories,
        {
          categoryName: "New Category",
          distance: 0,
          elevationGain: 0,
          cot: 0,
          price: 0,
          foreignerPrice: 0,
          date: "",
          time: "",
          description: "",
          gpxFileUrl: "",
          mapUrl: "",
          isRegistrationOpen: false,
          rundown: [],
          qualification: [],
          raceInclusions: [],
          mandatoryGears: [],
        },
      ],
    }));
  };

  const handleDeleteCategory = (index) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      const updatedCategories = [...eventData.categories];
      updatedCategories.splice(index, 1);
      setEventData((prev) => ({
        ...prev,
        categories: updatedCategories,
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, "semuaKegiatan", EVENT_ID);
      const payload = {
          ...eventData,
          categories: eventData.categories.map(cat => ({
              ...cat,
              price: Number(cat.price),
              foreignerPrice: Number(cat.foreignerPrice),
              distance: Number(cat.distance),
              elevationGain: Number(cat.elevationGain),
              cot: Number(cat.cot)
          }))
      };

      await updateDoc(docRef, payload);
      const closedCategories = payload.categories
        .filter((category) => category.isRegistrationOpen !== true)
        .map((category) => category.categoryName);
      if (closedCategories.length > 0) {
        const token = await getIdToken();
        await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/api/event/close-payments`,
          { eventId: EVENT_ID, categories: closedCategories },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      showNotification("success", "Event, categories, and rundown updated successfully");
    } catch (error) {
      console.error("Error updating event:", error);
      showNotification("error", "Failed to update event");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading event data...</div>;
  if (!eventData) return <div className="p-8 text-center text-red-500">Event not found.</div>;

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200">
            <LuSettings size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-start gap-4">
                <div>
                    <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                        <LuCalendarDays size={14} /> Event Settings
                    </p>
                    <h1 className="text-2xl md:text-3xl font-bold mb-1">Event Management</h1>
                    <p className="text-slate-500 text-sm max-w-xl">
                        Manage information of <strong>ULTRA24</strong>
                    </p>
                </div>
                <div className="flex flex-row gap-3 flex-shrink-0">
                    <Button variant="secondary" onClick={handleAddCategory}>
                        <LuPlus /> Add Category
                    </Button>
                    <Button variant="none" onClick={handleSave} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white">
                        {saving ? <><LuLoader className="animate-spin" /> Saving...</> : <><LuSave /> Save Changes</>}
                    </Button>
                </div>
            </div>
         </div>
      </div>

      {/* Main Info */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setIsGeneralInfoExpanded(!isGeneralInfoExpanded)}
        >
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${isGeneralInfoExpanded ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}>
              {isGeneralInfoExpanded ? <LuChevronUp size={20} /> : <LuChevronDown size={20} />}
            </div>
            <h2 className="text-sm uppercase font-semibold text-gray-800">General Information</h2>
          </div>
        </div>

        {isGeneralInfoExpanded && (
          <div className="p-4 border-t border-slate-100 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Event Title"
                name="title"
                value={eventData.title || ""}
                onChange={handleChange}
                labelColor="text-slate-700"
                labelClassName="text-sm font-medium"
              />
              <InputField
                type="select"
                label="Event Status"
                name="status"
                value={eventData.status || "Registrasi Dibuka"}
                onChange={handleChange}
                options={[
                    { value: "Registrasi Dibuka", label: "Registrasi Dibuka" },
                    { value: "Registrasi Ditutup", label: "Registrasi Ditutup" },
                    { value: "Akan Datang", label: "Akan Datang" },
                    { value: "Selesai", label: "Selesai" },
                ]}
                labelColor="text-slate-700"
                labelClassName="text-sm font-medium"
              />
              <InputField
                label="Date String"
                name="eventDateTimestamp"
                value={eventData.eventDateTimestamp || ""}
                onChange={handleChange}
                labelColor="text-slate-700"
                labelClassName="text-sm font-medium"
              />
              <InputField
                label="Location"
                name="location"
                value={eventData.location || ""}
                onChange={handleChange}
                labelColor="text-slate-700"
                labelClassName="text-sm font-medium"
              />
            </div>
            <InputField
                type="textarea"
                label="Description"
                name="aboutEvent"
                value={eventData.aboutEvent || ""}
                onChange={handleChange}
                rows={4}
                labelColor="text-slate-700"
                labelClassName="text-sm font-medium"
            />
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-800 px-1">Categories ({eventData.categories?.length || 0})</h2>
        <div className="grid grid-cols-1 gap-4">
            {eventData.categories?.map((cat, index) => (
                <div key={index} className="bg-white rounded-xl border border-slate-200 overflow-hidden relative transition-all duration-300">
                    {/* Category Header (Always Visible) */}
                    <div 
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleCategory(index)}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${expandedCategories[index] ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                {expandedCategories[index] ? <LuChevronUp size={20} /> : <LuChevronDown size={20} />}
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-slate-800">{cat.categoryName || "New Category"}</h3>
                                <div className="flex items-center gap-3 text-xs font-bold text-slate-400">
                                    <span>{cat.distance} KM</span>
                                    <span>•</span>
                                    <span className={cat.isRegistrationOpen ? "text-slate-400 font-medium" : "text-red-500 font-medium"}>
                                        {cat.isRegistrationOpen ? "Reg. Open" : "Reg. Closed"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                             {/* Status Toggle (Stop Propagation to prevent fold) */}
                            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                                <button
                                    onClick={() => handleToggleCategoryStatus(index)}
                                    className={`w-9 h-5 rounded-full transition-colors relative 
                                        ${cat.isRegistrationOpen ? 'bg-lime-500' : 'bg-slate-200'}`}
                                >
                                    <div
                                        className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all
                                        ${cat.isRegistrationOpen ? 'left-5' : 'left-1'}`}
                                    />
                                </button>
                            </div>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCategory(index);
                                }}
                                className="text-slate-300 hover:text-red-500 p-2 transition-colors z-10"
                                title="Delete Category"
                            >
                                <LuTrash2 size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Collapsible Content */}
                    {expandedCategories[index] && (
                        <div className="border-t border-slate-100">
                             <div className="p-4">
                                <div className="flex flex-col md:flex-row gap-4">
                            {/* Basic Info */}
                            <div className="md:w-1/3 space-y-4 border-r md:pr-6 border-slate-100">
                                <InputField
                                    label="Category Name"
                                    value={cat.categoryName}
                                    onChange={(e) => handleCategoryChange(index, "categoryName", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <InputField
                                    type="number"
                                    label="Distance (km)"
                                    value={cat.distance}
                                    onChange={(e) => handleCategoryChange(index, "distance", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <InputField
                                    type="number"
                                    label="Elevation Gain (m)"
                                    value={cat.elevationGain}
                                    onChange={(e) => handleCategoryChange(index, "elevationGain", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <InputField
                                    type="number"
                                    label="COT (Hours)"
                                    value={cat.cot}
                                    onChange={(e) => handleCategoryChange(index, "cot", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                            </div>

                            {/* Details */}
                            <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <InputField
                                    type="number"
                                    label="Price (IDR)"
                                    value={cat.price}
                                    onChange={(e) => handleCategoryChange(index, "price", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <InputField
                                    type="number"
                                    label="Foreigner Price (IDR)"
                                    value={cat.foreignerPrice}
                                    onChange={(e) => handleCategoryChange(index, "foreignerPrice", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <InputField
                                    type="date"
                                    label="Date (YYYY-MM-DD)"
                                    value={cat.date}
                                    onChange={(e) => handleCategoryChange(index, "date", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <InputField
                                    type="time"
                                    label="Time (HH:MM)"
                                    value={cat.time}
                                    onChange={(e) => handleCategoryChange(index, "time", e.target.value)}
                                    labelColor="text-slate-500"
                                    labelClassName="text-xs font-bold uppercase mb-1"
                                />
                                <div className="col-span-1 sm:col-span-2">
                                    <InputField
                                        type="textarea"
                                        label="Description"
                                        value={cat.description}
                                        onChange={(e) => handleCategoryChange(index, "description", e.target.value)}
                                        rows={2}
                                        labelColor="text-slate-500"
                                        labelClassName="text-xs font-bold uppercase mb-1"
                                    />
                                </div>
                                <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <InputField
                                        label="GPX File URL"
                                        value={cat.gpxFileUrl || ""}
                                        onChange={(e) => handleCategoryChange(index, "gpxFileUrl", e.target.value)}
                                        placeholder="https://..."
                                        labelColor="text-slate-500"
                                        labelClassName="text-xs font-bold uppercase mb-1"
                                    />
                                    <InputField
                                        label="Map URL"
                                        value={cat.mapUrl || ""}
                                        onChange={(e) => handleCategoryChange(index, "mapUrl", e.target.value)}
                                        placeholder="https://..."
                                        labelColor="text-slate-500"
                                        labelClassName="text-xs font-bold uppercase mb-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RUNDOWN SECTION */}
                    <div className="bg-slate-50 p-4">
                        <h3 className="text-sm font-bold text-slate-700 uppercase mb-4 flex items-center">
                            <LuActivity className="mr-2" />
                            Rundown Schedule
                        </h3>
                        
                        {/* List Existing Rundown */}
                        <div className="space-y-2 mb-4">
                            {!cat.rundown || cat.rundown.length === 0 ? (
                                <p className="text-sm text-slate-400 italic">No rundown items yet.</p>
                            ) : (
                                cat.rundown.map((item, rIndex) => (
                                    <div key={rIndex} className="grid grid-cols-12 gap-3 items-center bg-white p-3 rounded border border-slate-200 text-sm hover:border-slate-300 transition-colors">
                                        {/* Date */}
                                        <div className="col-span-6 sm:col-span-3 lg:col-span-2 text-slate-500 flex items-center gap-2">
                                            <LuCalendarDays size={14} className="text-slate-400 shrink-0" />
                                            <span className="truncate">{item.date || "-"}</span>
                                        </div>
                                        
                                        {/* Time */}
                                        <div className="col-span-6 sm:col-span-2 lg:col-span-1 text-center font-mono text-slate-600 bg-slate-50 border border-slate-100 px-2 py-1 rounded">
                                            {item.time}
                                        </div>

                                        {/* Activity */}
                                        <div className="col-span-12 sm:col-span-7 lg:col-span-6 font-medium text-slate-800 flex items-center gap-2">
                                            <span className="truncate">{item.activity}</span>
                                        </div>

                                        {/* Location */}
                                        <div className="col-span-10 sm:col-span-11 lg:col-span-2 text-slate-500 flex items-center gap-2 truncate">
                                            {item.location && (
                                                <>
                                                    <LuMapPin size={14} className="text-slate-400 shrink-0" />
                                                    <span className="truncate">{item.location}</span>
                                                </>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="col-span-2 sm:col-span-1 lg:col-span-1 flex justify-end items-center gap-1">
                                            <button 
                                                onClick={() => handleEditRundown(index, rIndex)}
                                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                                title="Edit Item"
                                            >
                                                <LuPencil size={14} />
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteRundown(index, rIndex)}
                                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                                title="Remove Item"
                                            >
                                                <LuTrash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Add New Rundown Form */}
                        <div className={`grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-3 rounded-xl border transition-colors ${
                            editingRundownIndices[index] !== undefined 
                                ? 'bg-slate-50 border-slate-300' 
                                : 'bg-slate-50 border-slate-200'
                        }`}>
                             <div className="sm:col-span-3">
                                <InputField
                                    type="date"
                                    label="Date"
                                    value={newRundownInputs[index]?.date || ""}
                                    onChange={(e) => handleRundownInputChange(index, "date", e.target.value)}
                                    labelColor="text-slate-700"
                                    labelClassName="text-xs font-bold mb-1"
                                />
                            </div>
                             <div className="sm:col-span-2">
                                <InputField
                                    type="time"
                                    label="Time"
                                    value={newRundownInputs[index]?.time || ""}
                                    onChange={(e) => handleRundownInputChange(index, "time", e.target.value)}
                                    labelColor="text-slate-700"
                                    labelClassName="text-xs font-bold mb-1"
                                />
                            </div>
                            <div className="sm:col-span-4">
                                <InputField
                                    label="Activity"
                                    placeholder="e.g., Flag Off"
                                    value={newRundownInputs[index]?.activity || ""}
                                    onChange={(e) => handleRundownInputChange(index, "activity", e.target.value)}
                                    labelColor="text-slate-700"
                                    labelClassName="text-xs font-bold mb-1"
                                />
                            </div>
                            <div className="sm:col-span-3">
                                <InputField
                                    label="Location (Opt)"
                                    placeholder="e.g., Main Gate"
                                    value={newRundownInputs[index]?.location || ""}
                                    onChange={(e) => handleRundownInputChange(index, "location", e.target.value)}
                                    labelColor="text-slate-700"
                                    labelClassName="text-xs font-bold mb-1"
                                />
                            </div>
                             <div className="sm:col-span-3">
                                <label className="invisible block text-xs font-bold mb-1">Action</label>
                                {editingRundownIndices[index] !== undefined ? (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="none"
                                            onClick={() => handleUpdateRundown(index)}
                                            className="flex-1 bg-red-600 hover:bg-lime-700 text-white flex items-center justify-center gap-2"
                                        >
                                            <LuSave size={16} /> Update
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleCancelEdit(index)}
                                            className="w-12 flex items-center justify-center !px-0"
                                        >
                                            <LuX size={16} />
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        variant="none"
                                        onClick={() => handleAddRundown(index)}
                                        className="w-full bg-slate-800 hover:bg-slate-900 text-white flex items-center justify-center gap-2"
                                    >
                                        <LuPlus /> Add Item
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* QUALIFICATION SECTION */}
                    <div className="p-5 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Qualification Requirements
                            </h3>
                        </div>
                        <div className="space-y-2 mb-3">
                            {!cat.qualification || cat.qualification.length === 0 ? (
                                <p className="text-sm text-slate-400">Belum ada requirement.</p>
                            ) : (
                                cat.qualification.map((item, qIndex) => (
                                    <div key={qIndex} className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl text-sm group">
                                        <span className="flex-1 text-slate-700">{item}</span>
                                        <button 
                                            onClick={() => {
                                                const updated = [...eventData.categories];
                                                updated[index].qualification = updated[index].qualification.filter((_, i) => i !== qIndex);
                                                setEventData(prev => ({ ...prev, categories: updated }));
                                            }}
                                            className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <LuTrash2 size={14} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <InputField
                                    placeholder="Add qualification requirement..."
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                            const updated = [...eventData.categories];
                                            updated[index].qualification = [...(updated[index].qualification || []), e.target.value.trim()];
                                            setEventData(prev => ({ ...prev, categories: updated }));
                                            e.target.value = '';
                                        }
                                    }}
                                />
                            </div>
                            <Button
                                variant="none"
                                onClick={(e) => {
                                    // Navigate to the input inside the wrapper
                                    const wrapper = e.currentTarget.previousSibling;
                                    const input = wrapper.querySelector('input');
                                    if (input && input.value.trim()) {
                                        const updated = [...eventData.categories];
                                        updated[index].qualification = [...(updated[index].qualification || []), input.value.trim()];
                                        setEventData(prev => ({ ...prev, categories: updated }));
                                        input.value = '';
                                    }
                                }}
                                className="bg-slate-800 hover:bg-slate-700 text-white !px-4"
                            >
                                <LuPlus />
                            </Button>
                        </div>
                    </div>

                    {/* RACE INCLUSIONS SECTION */}
                    <div className="p-5 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Race Inclusions
                            </h3>
                            <span className="text-xs text-slate-400">
                                {(cat.raceInclusions || []).length} item dipilih
                            </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {[
                                "Nomor BIB",
                                "Medali Finisher", 
                                "Jersey Finisher",
                                "Race Jersey",
                                "Merchandise",
                                "Finisher Certificate"
                            ].map((inclusion) => {
                                const isIncluded = (cat.raceInclusions || []).includes(inclusion);
                                return (
                                    <label 
                                        key={inclusion}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-all ${
                                            isIncluded 
                                                ? 'bg-slate-800 text-white' 
                                                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isIncluded}
                                            onChange={() => {
                                                const updated = [...eventData.categories];
                                                const currentInclusions = updated[index].raceInclusions || [];
                                                if (isIncluded) {
                                                    updated[index].raceInclusions = currentInclusions.filter(i => i !== inclusion);
                                                } else {
                                                    updated[index].raceInclusions = [...currentInclusions, inclusion];
                                                }
                                                setEventData(prev => ({ ...prev, categories: updated }));
                                            }}
                                            className="sr-only"
                                        />
                                        <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                            isIncluded ? 'border-white bg-white' : 'border-slate-300'
                                        }`}>
                                            {isIncluded && <span className="w-2 h-2 bg-slate-800 rounded-sm"></span>}
                                        </span>
                                        <span className="text-sm">{inclusion}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* MANDATORY GEARS SECTION */}
                    <div className="p-5 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Mandatory Gears
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                                <span className="flex items-center gap-1">
                                    <span className="w-5 h-5 bg-slate-800 text-white rounded text-[10px] flex items-center justify-center font-bold">M</span>
                                    Wajib
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-5 h-5 bg-slate-400 text-white rounded text-[10px] flex items-center justify-center font-bold">R</span>
                                    Rekomendasi
                                </span>
                            </div>
                        </div>
                        <div className="space-y-1">
                            {[
                                "Running Vest/Belt",
                                "Mobile Phone with GPX Route",
                                "Smartwatch",
                                "Minimum Water Supply of 500ml",
                                "Foldable Cup",
                                "Survival Blanket",
                                "First Aid Kits",
                                "Snack/Energy Bar",
                                "Windproof Jacket",
                                "Rainproof Jacket/Poncho",
                                "ID Card",
                                "Whistle",
                                "Headlamp",
                                "Cap/Bandana",
                                "Running Shoes",
                                "Sufficient Cash",
                                "Trekking Poles"
                            ].map((gearName) => {
                                const currentGears = cat.mandatoryGears || [];
                                const existingGear = currentGears.find(g => g.gear === gearName);
                                const isMandatory = existingGear?.mandatory || false;
                                const isRecommended = existingGear?.recommended || false;
                                
                                const updateGear = (type) => {
                                    const updated = [...eventData.categories];
                                    let gears = [...(updated[index].mandatoryGears || [])];
                                    const gearIndex = gears.findIndex(g => g.gear === gearName);
                                    
                                    if (type === 'mandatory') {
                                        if (isMandatory) {
                                            gears = gears.filter(g => g.gear !== gearName);
                                        } else {
                                            if (gearIndex >= 0) {
                                                gears[gearIndex] = { gear: gearName, mandatory: true, recommended: false };
                                            } else {
                                                gears.push({ gear: gearName, mandatory: true, recommended: false });
                                            }
                                        }
                                    } else if (type === 'recommended') {
                                        if (isRecommended) {
                                            gears = gears.filter(g => g.gear !== gearName);
                                        } else {
                                            if (gearIndex >= 0) {
                                                gears[gearIndex] = { gear: gearName, mandatory: false, recommended: true };
                                            } else {
                                                gears.push({ gear: gearName, mandatory: false, recommended: true });
                                            }
                                        }
                                    }
                                    
                                    updated[index].mandatoryGears = gears;
                                    setEventData(prev => ({ ...prev, categories: updated }));
                                };

                                return (
                                    <div key={gearName} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                                        <span className="text-sm text-slate-700">{gearName}</span>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => updateGear('recommended')}
                                                className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold transition-colors ${
                                                    isRecommended ? 'bg-slate-400 text-white' : 'bg-slate-100 text-slate-300 hover:bg-slate-200'
                                                }`}
                                                title="Recommended"
                                            >
                                                R
                                            </button>
                                            <button 
                                                onClick={() => updateGear('mandatory')}
                                                className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold transition-colors ${
                                                    isMandatory ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-300 hover:bg-slate-200'
                                                }`}
                                                title="Mandatory"
                                            >
                                                M
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            </div>
        ))}
        </div>
      </div>
    </div>
  );
};
